import React, { useEffect, useState } from "react";
import { FiCheck, FiChevronDown, FiLink2, FiShare2, FiUsers, FiAward, FiShield, FiEye, FiHeart } from "react-icons/fi";
import ArdrendHeader from "../../components/AllVon/VonHeader/ArdrendHeader";
import ArdrendFooter from "../../components/AllVon/VonFooter/ArdrendFooter";
import { siteStats } from "../../data/siteStats";
import ApplyForm from "./ApplyForm";

/**
 * Community Learning Advisor (CLA) programme: partners earn 5% of the onboarding fee per paid
 * enrolment through their referral link, paid for each completed set of five.
 */

const CONTACT_EMAIL = "info@e1xacademy.net";
const CONTACT_PHONE = "+1 240 906 4083";

const RATE = 0.05;
const BLOCK = 5;
const REGIONS = [
  { key: "ng", label: "Nigeria", fee: 25000, symbol: "₦" },
  { key: "af", label: "Rest of Africa", fee: 25, symbol: "$" },
  { key: "row", label: "US & rest of world", fee: 120, symbol: "$" },
];
const money = (symbol, n) => `${symbol}${n.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: n % 1 ? 2 : 0 })}`;
const programmeCount = siteStats.find((s) => s.key === "programs")?.value ?? 19;

const TRUST = [
  ["ACTD", "Accredited institution"],
  ["13 weeks", "Live, instructor-led"],
  [`${programmeCount}`, "Career programmes"],
  ["5%", "Commission per enrolment"],
];

const WHY = [
  [FiAward, "An offer that sells itself", "Accredited 13-week training in AI, data, software, design and digital marketing, at a price the people in your community can actually reach."],
  [FiEye, "Attribution you can check", "Every enrolment is tracked to your personal link and recorded against your name. You can confirm your count with your E1X contact at any time."],
  [FiHeart, "Impact you can see", "You're not selling a product. You're opening a door for someone who had no route in, and you stay connected to them through the cohort."],
];

const STEPS = [
  [FiUsers, "Apply", "Complete the CLA form. We'll confirm your role and send your onboarding pack and unique referral link."],
  [FiShare2, "Share", "Use your link and our ready-made brochures, graphics and video clips to reach people in your community."],
  [FiLink2, "Guide", "Walk applicants through registration so their enrolment is attributed to you, and support them as they start."],
  [FiShield, "Earn", "Hit a milestone, confirm your count with your E1X contact, and get paid on the next disbursement day."],
];

const WHO = [
  ["Educators & youth mentors", "Teachers, lecturers and mentors who see talent going unused every term."],
  ["Community & faith leaders", "NGO leaders, church and mosque leaders, estate and association heads."],
  ["Professionals & students", "Anyone with a network, a reputation worth protecting, and a reason to care."],
];

const PROGRAMMES = [
  ["Data & Analytics", ["Data Analytics for Business Professionals", "Data Analytics", "Data Science", "Data Engineering"]],
  ["Artificial Intelligence", ["AI & Automation", "AI & Machine Learning"]],
  ["Software & Engineering", ["Software Development", "Front-End", "Back-End", "DevOps Engineering", "Cybersecurity"]],
  ["Design & Product", ["UI/UX Design", "AI-Assisted Graphic Design", "AI Product Management"]],
  ["Marketing & Business", ["Data-Driven Digital Marketing", "Social Media Marketing", "AI Project Management", "AI Content Creation", "AI Virtual Assistant"]],
];

const FAQ = [
  ["Why do I need five enrolments before my first payout?", "The milestone structure rewards sustained recruitment rather than one-off referrals. Nothing is lost while you build toward it. Every paid enrolment you bring is recorded against your name from the day it comes in and counts toward your next completed set of five."],
  ["Am I an employee of E1X Academy?", "No. This is a performance-based partnership. You operate as an independent community partner and earn on verified, paid enrolments that result from your own recruitment efforts."],
  ["How do I know an enrolment was credited to me?", "Every applicant you refer must go through the official E1X registration process using your link, which is what ties the enrolment to your name. Your point of contact can confirm your current count at any time, and you should check it before each disbursement window."],
  ["What counts as a paid enrolment?", "A learner who has completed registration through your link and paid their onboarding fee in full. Applications and expressions of interest don't count until payment is received."],
  ["Do I need a large social media following?", "No. Most of our strongest CLAs work through personal networks, classrooms, congregations and community groups rather than public platforms. Trust converts better than reach."],
  ["What marketing materials do I get?", "Brochures, design assets and video clips, shared as folders that we update regularly with new material. You're free to create your own content as well, provided it represents the programme accurately."],
];

const Kicker = ({ children }) => (
  <div className="text-primary text-[12px] font-bold tracking-[0.25em] uppercase mb-4">{children}</div>
);

const SectionHead = ({ kicker, title, lede, light }) => (
  <div className="max-w-3xl">
    {kicker && <Kicker>{kicker}</Kicker>}
    <h2 className={`text-3xl md:text-[44px] font-bold leading-[1.1] tracking-tight ${light ? "text-white" : "text-[#0b0f19]"}`}>{title}</h2>
    {lede && <p className={`mt-5 text-base md:text-lg leading-relaxed ${light ? "text-white/70" : "text-gray-600"}`}>{lede}</p>}
  </div>
);

const ArrowBtn = ({ href, children }) => (
  <a href={href} className="bg-primary hover:bg-[#b0451e] text-white transition-colors rounded-full pl-5 pr-2 py-2 inline-flex items-center gap-3 group shadow-md justify-between">
    <span className="text-[11px] font-bold uppercase tracking-wider">{children}</span>
    <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-primary shrink-0">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </span>
  </a>
);

/** Five dots, filled up to `filled`: one set of five enrolments. */
const BlockDots = ({ filled, size = "w-3.5 h-3.5", dim = "bg-white/20" }) => (
  <div className="flex gap-1.5">
    {Array.from({ length: BLOCK }, (_, i) => (
      <span key={i} className={`${size} rounded-full transition-colors duration-300 ${i < filled ? "bg-primary" : dim}`} />
    ))}
  </div>
);

function HeroCard() {
  return (
    <div className="relative w-full max-w-md lg:ml-auto">
      <div className="absolute -inset-6 bg-primary/20 blur-3xl rounded-full" aria-hidden="true" />
      <div className="relative rounded-3xl border border-white/10 bg-white/[0.06] backdrop-blur-md p-6 md:p-7 text-white shadow-2xl">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">Your progress</span>
          <span className="text-[11px] font-bold uppercase tracking-wider bg-primary/20 text-primary rounded-full px-3 py-1">8 paid enrolments</span>
        </div>
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold">Set 1</div>
              <div className="text-xs text-white/50">Complete · paid out</div>
            </div>
            <BlockDots filled={5} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold">Set 2</div>
              <div className="text-xs text-white/50">3 of 5 · carried forward</div>
            </div>
            <BlockDots filled={3} />
          </div>
        </div>
        <div className="mt-6 pt-5 border-t border-white/10 flex items-end justify-between">
          <div>
            <div className="text-xs text-white/50">Commission</div>
            <div className="text-4xl font-bold tracking-tight text-primary">5%</div>
          </div>
          <div className="text-right text-xs text-white/50 max-w-[12rem]">of the onboarding fee, for every paid enrolment through your link</div>
        </div>
      </div>
    </div>
  );
}

function Calculator() {
  const [region, setRegion] = useState(REGIONS[0]);
  const [count, setCount] = useState(8);
  const per = region.fee * RATE;
  const sets = Math.floor(count / BLOCK);
  const paid = sets * BLOCK;
  const carry = count - paid;

  return (
    <div className="rounded-3xl bg-[#0b0f19] text-white p-6 md:p-8 shadow-xl">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">Work out your payout</div>

      <div className="mt-5 grid grid-cols-3 gap-1 rounded-full bg-white/10 p-1" role="radiogroup" aria-label="Learner region">
        {REGIONS.map((r) => (
          <button
            key={r.key}
            type="button"
            role="radio"
            aria-checked={region.key === r.key}
            onClick={() => setRegion(r)}
            className={`rounded-full px-2 py-2 text-[11px] md:text-xs font-bold transition-colors ${region.key === r.key ? "bg-white text-[#0b0f19]" : "text-white/70 hover:text-white"}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="mt-7">
        <div className="flex items-baseline justify-between">
          <label htmlFor="cla-count" className="text-sm text-white/70">Paid enrolments</label>
          <span className="text-3xl font-bold tabular-nums">{count}</span>
        </div>
        <input
          id="cla-count"
          type="range"
          min={0}
          max={50}
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          className="mt-3 w-full accent-[#d15424]"
        />
      </div>

      <div className="mt-6 flex flex-wrap gap-x-3 gap-y-2">
        {Array.from({ length: Math.max(1, Math.ceil(count / BLOCK)) }, (_, i) => (
          <BlockDots key={i} filled={Math.min(BLOCK, Math.max(0, count - i * BLOCK))} size="w-2.5 h-2.5" />
        ))}
      </div>

      <div className="mt-7 grid grid-cols-2 gap-4 border-t border-white/10 pt-6">
        <div>
          <div className="text-xs text-white/50">Paid out ({sets} {sets === 1 ? "set" : "sets"} of five)</div>
          <div className="mt-1 text-3xl font-bold text-primary tabular-nums">{money(region.symbol, paid * per)}</div>
        </div>
        <div>
          <div className="text-xs text-white/50">Carried forward</div>
          <div className="mt-1 text-3xl font-bold tabular-nums">{carry}<span className="text-base font-semibold text-white/50"> / 5</span></div>
        </div>
      </div>
      <p className="mt-5 text-xs text-white/50 leading-relaxed">
        {money(region.symbol, per)} per enrolment (5% of the {money(region.symbol, region.fee)} onboarding fee).
        {count < BLOCK ? " Your first payout unlocks at five paid enrolments." : ""} Estimate only: commission is calculated on the fee each learner actually paid.
      </p>
    </div>
  );
}

function FaqItem({ q, a, open, onToggle }) {
  return (
    <div className="border-b border-gray-200">
      <button type="button" onClick={onToggle} aria-expanded={open} className="w-full flex items-center justify-between gap-6 py-5 text-left">
        <span className="font-semibold text-[#0b0f19] md:text-lg">{q}</span>
        <FiChevronDown className={`shrink-0 text-primary text-xl transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"}`}>
        <p className="overflow-hidden text-gray-600 leading-relaxed max-w-3xl">{a}</p>
      </div>
    </div>
  );
}

const PartnerProgram = () => {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Community Learning Advisor Programme | E1X Academy";
  }, []);

  return (
    <div className="min-h-full flex flex-col font-sans bg-white text-[#0b0f19]">
      <main className="flex-grow">
        <ArdrendHeader />

        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0b0f19]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0b0f19] via-[#1a2333] to-[#05070c]" aria-hidden="true" />
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full border-[60px] border-white/[0.03]" aria-hidden="true" />
          <div className="relative z-10 max-w-[1560px] mx-auto px-6 pt-36 pb-20 md:pt-44 md:pb-28 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
            <div>
              <Kicker>Community Learning Advisor Network</Kicker>
              <h1 className="text-white text-4xl md:text-6xl lg:text-[68px] font-bold max-w-3xl leading-[1.05] tracking-tight">
                Earn 5% for every learner you bring to E1X Academy
              </h1>
              <p className="mt-7 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
                You already know people who want a way into tech and can't find one they can afford. Introduce them to E1X,
                and earn on every enrolment that comes through your link, while they get an accredited qualification for a
                fraction of what it normally costs.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <ArrowBtn href="#apply">Become a CLA</ArrowBtn>
                <a href="#how-it-works" className="hover:bg-white/10 text-white rounded-full px-5 py-3 inline-flex items-center justify-center text-[11px] font-bold border border-white/30 transition-colors uppercase tracking-wider">
                  See how it works
                </a>
              </div>
            </div>
            <HeroCard />
          </div>

          {/* Trust bar */}
          <div className="relative z-10 border-t border-white/10">
            <div className="max-w-[1560px] mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
              {TRUST.map(([value, label]) => (
                <div key={label} className="text-center md:text-left">
                  <div className="text-white text-2xl md:text-3xl font-bold tracking-tight">{value}</div>
                  <div className="text-white/50 text-xs md:text-sm mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-[1560px] mx-auto px-6">
            <SectionHead
              kicker="Why CLAs"
              title="Built for people with real community trust"
              lede="This is not a banner-ad affiliate scheme. CLAs are educators, mentors and community leaders who recommend E1X to people who already trust them, and who are paid for the enrolments that recommendation produces."
            />
            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {WHY.map(([Icon, title, body]) => (
                <div key={title} className="group rounded-3xl border border-gray-200 p-8 hover:border-primary/40 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 text-primary flex items-center justify-center text-xl group-hover:bg-primary group-hover:text-white transition-colors">
                    <Icon />
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{title}</h3>
                  <p className="mt-3 text-gray-600 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="py-20 md:py-28 bg-gray-50 scroll-mt-20">
          <div className="max-w-[1560px] mx-auto px-6">
            <SectionHead kicker="How it works" title="Four steps from application to payout" />
            <ol className="list-none pl-0 mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
              {STEPS.map(([Icon, title, body], i) => (
                <li key={title} className="relative rounded-3xl bg-white border border-gray-200 p-8">
                  <div className="flex items-center justify-between">
                    <span className="text-5xl font-bold text-gray-100 leading-none select-none">0{i + 1}</span>
                    <span className="w-11 h-11 rounded-full bg-primary text-white flex items-center justify-center text-lg"><Icon /></span>
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{title}</h3>
                  <p className="mt-3 text-gray-600 leading-relaxed">{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Earnings */}
        <section id="earnings" className="py-20 md:py-28 bg-white scroll-mt-20">
          <div className="max-w-[1560px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <SectionHead
                kicker="What you earn"
                title="5% of the onboarding fee, paid in sets of five"
                lede="You earn on every verified, paid enrolment attributed to you. Payment is tied to milestones rather than to individual applications: you're paid for each completed set of five paid enrolments."
              />
              <ul className="mt-8 space-y-4">
                {[
                  "Your first payout unlocks at five paid enrolments",
                  "Paid for every completed set of five, at any volume",
                  "Enrolments beyond a completed set carry forward to the next one",
                  "Every enrolment must come through your unique referral link to be attributed to you",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-orange-50 text-primary flex items-center justify-center"><FiCheck /></span>
                    <span className="text-gray-700">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 overflow-hidden rounded-2xl border border-gray-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-orange-50 text-[11px] uppercase tracking-wider text-[#0b0f19]">
                    <tr><th className="px-5 py-3 font-bold">Paid enrolments</th><th className="px-5 py-3 font-bold">What you earn</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr><td className="px-5 py-4 font-bold whitespace-nowrap">0–4</td><td className="px-5 py-4 text-gray-600">Not yet eligible for payout. Your enrolments build toward your first set of five.</td></tr>
                    <tr><td className="px-5 py-4 font-bold whitespace-nowrap">5 and above</td><td className="px-5 py-4 text-gray-600">5% of the onboarding fee per enrolment, paid for each completed set of five.</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-5 text-sm text-gray-500 leading-relaxed">
                Onboarding fees vary by region: ₦25,000 in Nigeria, $25 elsewhere in Africa, and $120 for the US and the rest
                of the world. Your commission is calculated on the fee each learner actually paid.
              </p>
            </div>

            <div className="lg:sticky lg:top-28 space-y-6">
              <Calculator />
              <div className="rounded-3xl border-l-4 border-primary bg-orange-50 p-6">
                <div className="font-bold">A worked example</div>
                <p className="mt-2 text-gray-700 leading-relaxed">
                  You bring in eight paid enrolments. You're paid on the first completed set of five, and the remaining three
                  carry forward toward your next set. Nothing is lost while you build.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Getting paid */}
        <section className="py-20 md:py-28 bg-gray-50">
          <div className="max-w-[1560px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <SectionHead
                kicker="Getting paid"
                title="Paid every Thursday and Friday"
                lede="Once you reach a qualifying milestone, your payout is processed on the next available disbursement day rather than immediately on the day you hit it."
              />
              <p className="mt-5 text-gray-600 leading-relaxed max-w-2xl">
                Confirm your enrolment count with your E1X point of contact ahead of each window if you believe you've reached a
                new milestone. It's the fastest way to make sure nothing is missed.
              </p>
            </div>
            <div className="rounded-3xl bg-white border border-gray-200 p-8 md:p-10">
              <h3 className="text-xl font-bold">What we give you</h3>
              <ul className="mt-6 space-y-4">
                {[
                  "A unique referral link, so every enrolment is attributed to you",
                  "A named point of contact for onboarding, tracking and support",
                  "Brochures, graphics and video clips, updated regularly",
                  "Access to the CLA WhatsApp community",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-primary text-white flex items-center justify-center text-sm"><FiCheck /></span>
                    <span className="text-gray-700">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Who */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-[1560px] mx-auto px-6">
            <SectionHead kicker="Who it's for" title="Who becomes a CLA" lede="If people in your community come to you for guidance, you already have what this role needs." />
            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {WHO.map(([title, body], i) => (
                <div key={title} className={`rounded-3xl p-8 ${i === 1 ? "bg-primary text-white" : "bg-[#0b0f19] text-white"}`}>
                  <h3 className="text-xl font-bold">{title}</h3>
                  <p className={`mt-3 leading-relaxed ${i === 1 ? "text-white/85" : "text-white/65"}`}>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Programmes */}
        <section className="py-20 md:py-28 bg-gray-50">
          <div className="max-w-[1560px] mx-auto px-6">
            <SectionHead
              kicker="What you'll recommend"
              title="Programmes worth recommending"
              lede="Five programme categories, all delivered live and online over 13 weeks, with recordings, real projects and an accredited certificate at the end."
            />
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PROGRAMMES.map(([title, items]) => (
                <div key={title} className="rounded-3xl bg-white border border-gray-200 p-8">
                  <h3 className="text-xl font-bold">{title}</h3>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {items.map((c) => (
                      <span key={c} className="text-xs font-semibold rounded-full bg-gray-100 text-gray-700 px-3 py-1.5">{c}</span>
                    ))}
                  </div>
                </div>
              ))}
              <div className="rounded-3xl bg-[#0b0f19] text-white p-8 flex flex-col justify-between">
                <FiAward className="text-3xl text-primary" />
                <div>
                  <h3 className="mt-6 text-xl font-bold">Accredited & recognised</h3>
                  <p className="mt-3 text-white/65 leading-relaxed">E1X Academy is accredited by the American Council of Training and Development (ACTD).</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-[1560px] mx-auto px-6 grid lg:grid-cols-[1fr_1.6fr] gap-14">
            <SectionHead kicker="FAQ" title="Questions CLAs ask" lede="Anything else? We're happy to talk it through before you apply." />
            <div className="border-t border-gray-200">
              {FAQ.map(([q, a], i) => (
                <FaqItem key={q} q={q} a={a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="apply" className="relative overflow-hidden bg-[#0b0f19] py-24 md:py-32 scroll-mt-20">
          <div className="absolute -bottom-48 -left-48 w-[560px] h-[560px] rounded-full border-[70px] border-white/[0.03]" aria-hidden="true" />
          <div className="absolute -top-24 right-0 w-96 h-96 bg-primary/20 blur-3xl rounded-full" aria-hidden="true" />
          <div className="relative max-w-[1560px] mx-auto px-6 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-start">
            <div className="lg:sticky lg:top-28">
              <Kicker>Apply</Kicker>
              <h2 className="text-white text-3xl md:text-5xl font-bold leading-[1.1] tracking-tight">Turn your influence into opportunity</h2>
              <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
                Every cohort, people who qualified and wanted a place don't take one because nobody they trusted told them it
                existed. Be the person who tells them.
              </p>
              <ul className="mt-8 space-y-3">
                {["We review every application personally", "Approved CLAs get a referral link and onboarding pack", "A named E1X contact supports you from day one"].map((t) => (
                  <li key={t} className="flex gap-3 text-white/80">
                    <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-primary/20 text-primary flex items-center justify-center"><FiCheck /></span>
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-white/60">
                Questions first? Email{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-white underline underline-offset-4 hover:text-primary">{CONTACT_EMAIL}</a>{" "}
                or call{" "}
                <a href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`} className="text-white underline underline-offset-4 hover:text-primary">{CONTACT_PHONE}</a>.
              </p>
            </div>
            <ApplyForm />
          </div>
        </section>
      </main>
      <ArdrendFooter />
    </div>
  );
};

export default PartnerProgram;
